import React from "react";
import { useNavigate } from "react-router-dom";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import useCart from "../../hooks/useCart";
import useWishlist from "../../hooks/useWishlist";
import "./ProductCard.css";

const LOW_STOCK_THRESHOLD = 5;
const DEFAULT_MEDIA_FRAME_PRESET = "balanced";

const getMediaFramePreset = (width, height) => {
  const safeWidth = Number(width);
  const safeHeight = Number(height);

  if (!Number.isFinite(safeWidth) || !Number.isFinite(safeHeight) || safeWidth <= 0 || safeHeight <= 0) {
    return DEFAULT_MEDIA_FRAME_PRESET;
  }

  const aspectRatio = safeWidth / safeHeight;

  if (aspectRatio <= 0.84) {
    return "portrait";
  }

  if (aspectRatio >= 1.2) {
    return "landscape";
  }

  return DEFAULT_MEDIA_FRAME_PRESET;
};

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart, isInCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const [addedToCart, setAddedToCart] = React.useState(false);
  const [inWishlist, setInWishlist] = React.useState(false);
  const [mediaFramePreset, setMediaFramePreset] = React.useState(DEFAULT_MEDIA_FRAME_PRESET);
  const videoRef = React.useRef(null);
  const primaryMedia = product?.images?.[0];

  const isVideo = (url) => {
    if (!url) {
      return false;
    }

    const videoExtensions = [".mp4", ".avi", ".mov", ".wmv", ".flv", ".webm"];
    return videoExtensions.some((extension) => url.toLowerCase().includes(extension));
  };

  const safePlayVideo = async (videoElement) => {
    if (!videoElement) {
      return;
    }

    try {
      await videoElement.play();
    } catch (error) {
      console.log("Video play prevented:", error.message);
    }
  };

  const safePauseVideo = (videoElement) => {
    if (!videoElement) {
      return;
    }

    try {
      videoElement.pause();
    } catch (error) {
      console.log("Video pause prevented:", error.message);
    }
  };

  const cleanupVideo = React.useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    safePauseVideo(videoRef.current);
    videoRef.current = null;
  }, []);

  React.useEffect(() => {
    if (product && product._id) {
      setInWishlist(isInWishlist(product._id));
    }
  }, [product, isInWishlist]);

  React.useEffect(() => {
    return () => {
      cleanupVideo();
    };
  }, [cleanupVideo]);

  React.useEffect(() => {
    setMediaFramePreset(DEFAULT_MEDIA_FRAME_PRESET);
  }, [primaryMedia]);

  const handlePrimaryMediaLoad = (event) => {
    setMediaFramePreset(
      getMediaFramePreset(
        event.currentTarget.naturalWidth,
        event.currentTarget.naturalHeight
      )
    );
  };

  const hasDiscount = product.price && product.offerPrice && product.price > product.offerPrice;
  const discountPercentage = hasDiscount
    ? Math.round(((product.price - product.offerPrice) / product.price) * 100)
    : 0;
  const displayBadge = product.badge || (hasDiscount ? "Offer" : "");
  const requiresSizeSelection = Array.isArray(product.availableSizes) && product.availableSizes.length > 0;
  const stockQuantity = Number.isFinite(Number(product.stockQuantity))
    ? Math.max(0, Math.floor(Number(product.stockQuantity)))
    : 0;
  const isOutOfStock = stockQuantity === 0;
  const isLowStock = stockQuantity > 0 && stockQuantity <= LOW_STOCK_THRESHOLD;
  const mediaFrameClassName = [
    "product-card-media-frame",
    `product-card-media-frame--${isVideo(primaryMedia) ? DEFAULT_MEDIA_FRAME_PRESET : mediaFramePreset}`,
  ].join(" ");

  return (
    <div
      className="product-card-container"
      onClick={() => navigate(`/product/${product._id}`)}
    >
      {displayBadge ? <span className="badge">{displayBadge}</span> : null}

      <div
        className="wishlist-icon"
        onClick={(event) => {
          event.stopPropagation();

          if (inWishlist) {
            removeFromWishlist(product._id);
            setInWishlist(false);
            return;
          }

          const success = addToWishlist(product);

          if (success) {
            setInWishlist(true);
          }
        }}
      >
        {inWishlist ? <FaHeart className="heart-filled" /> : <FaRegHeart />}
      </div>

      <div className={mediaFrameClassName}>
        {isVideo(primaryMedia) ? (
          <div className="video-thumbnail-wrapper">
            <video
              ref={videoRef}
              src={primaryMedia}
              className="product-video-thumbnail"
              muted
              onMouseEnter={(event) => {
                videoRef.current = event.target;
                safePlayVideo(event.target);
              }}
              onMouseLeave={(event) => {
                safePauseVideo(event.target);

                if (videoRef.current === event.target) {
                  videoRef.current = null;
                }
              }}
              onError={(event) => {
                console.log("Video error:", event.target.error);
              }}
            />
            <div className="video-play-indicator">Video</div>
          </div>
        ) : primaryMedia ? (
          <img
            className="product-card-media"
            src={primaryMedia}
            alt={product.title || "Product"}
            loading="lazy"
            decoding="async"
            draggable={false}
            onLoad={handlePrimaryMediaLoad}
          />
        ) : (
          <div className="product-image-placeholder">No Image</div>
        )}
      </div>

      <div className="product-info">
        <div className="product-pricing">
          <span className="discounted-pricee">{`\u20B9${product.offerPrice}`}</span>
          {product.price && product.price !== product.offerPrice ? (
            <>
              <span className="original-pricee">{`\u20B9${product.price}`}</span>
              <span className="discount-tag">{discountPercentage}% Off</span>
            </>
          ) : null}
        </div>

        <h3 className="product-name">{product.title}</h3>

        {product.offerPrice ? (
          <div className="offer-pricee">
            Get it for {`\u20B9${product.offerPrice}`}{" "}
            <span className="coupon-textt">with Discount</span>
          </div>
        ) : null}

        <div className="stock-note-slot">
          {isOutOfStock ? (
            <div className="stock-note stock-note--out">Out of stock</div>
          ) : isLowStock ? (
            <div className="stock-note stock-note--low">
              Only {stockQuantity} item(s) left in stock
            </div>
          ) : (
            <div className="stock-note stock-note--placeholder" aria-hidden="true">
              Inventory status
            </div>
          )}
        </div>

        {isOutOfStock ? (
          <button
            className="add-to-cart1 add-to-cart1--disabled"
            onClick={(event) => event.stopPropagation()}
            disabled
          >
            Out of Stock
          </button>
        ) : isInCart(product._id) ? (
          <button
            className="add-to-cart1"
            onClick={(event) => {
              event.stopPropagation();
              navigate("/cart");
            }}
          >
            Go to Cart
          </button>
        ) : (
          <button
            className="add-to-cart1"
            onClick={(event) => {
              event.stopPropagation();

              if (requiresSizeSelection) {
                navigate(`/product/${product._id}`);
                return;
              }

              const success = addToCart(product, 1);

              if (success) {
                setAddedToCart(true);
                setTimeout(() => setAddedToCart(false), 2000);
              }
            }}
          >
            {requiresSizeSelection ? "Select Size" : addedToCart ? "Added to Cart!" : "Add to Cart"}
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
